import { useEffect, useState } from 'react';
import { SearchResult } from 'app/concierge/types';
import { conciergeAPI, IReferenceData, ITileInfoData } from 'api/concierge';

export interface IResultItemsData {
    tilesInfo: ITileInfoData[];
    referencesData: IReferenceData[];
    /** Both lookups have answered (with rows, or with a logged failure and none). */
    settled: boolean;
}

const EMPTY: IResultItemsData = { tilesInfo: [], referencesData: [], settled: false };

const sameId = (a: unknown, b: unknown): boolean => Number(a ?? 0) === Number(b ?? 0);

export function findTileInfo(tilesInfo: ITileInfoData[], result: SearchResult): ITileInfoData | undefined {
    return tilesInfo.find(
        (info) => sameId(info.element_id, result.id) && sameId(info.segment_value_id, result.second_id),
    );
}

/** An element row renders only with its tile; every other result type renders from the result itself. */
export function renderableResults(items: SearchResult[], data: IResultItemsData): SearchResult[] {
    return items.filter((result) => result.type !== 'dashboard_element' || findTileInfo(data.tilesInfo, result));
}

/**
 * The tile and reference lookups behind a result list, resolved once per list. A caller that
 * already resolved them (the section deciding whether to render at all) passes them through so
 * the body never fetches twice. A failed lookup is logged and settles with no rows: the rows it
 * would have described stay unrendered instead of leaving a header over nothing.
 */
export default function useResultItemsData(items: SearchResult[], preloaded?: IResultItemsData): IResultItemsData {
    const [data, setData] = useState<IResultItemsData>(EMPTY);

    useEffect(() => {
        if (preloaded) {
            return;
        }
        const tileIds = items
            .filter((item) => item.type === 'dashboard_element')
            .map((item) => `${item.id}_${item.second_id}`);
        const referenceIds = items
            .filter((item) => item.type === 'external_report_reference')
            .map((item) => String(item.id));
        if (tileIds.length === 0 && referenceIds.length === 0) {
            setData({ ...EMPTY, settled: true });

            return;
        }
        let cancelled = false;
        const failed = (what: string) => (error: unknown) => {
            console.error(`Concierge search results: ${what} lookup failed`, error);

            return [];
        };
        Promise.all([
            tileIds.length ? conciergeAPI.getTilesInfo(tileIds).catch(failed('tile')) : [],
            referenceIds.length
                ? conciergeAPI.getExternalReportReferenceInfo(referenceIds).catch(failed('reference'))
                : [],
        ]).then(([tilesInfo, referencesData]) => {
            if (!cancelled) {
                setData({ tilesInfo: tilesInfo ?? [], referencesData: referencesData ?? [], settled: true });
            }
        });

        return () => {
            cancelled = true;
        };
    }, [items, preloaded]);

    return preloaded ?? data;
}
